import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { Upload, notification } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { formatNumber } from 'pages/star-shared/utils';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { useDataImport } from '../context';
import { ImportKind, ImportResult } from '../types';
import { MAX_FILE_MB, formatDay, formatPeriod, normalizeResult } from '../utils';
import ImportResultPanel from './import-result-panel';

interface Props {
  kind: ImportKind;
  /** MONTHLY: tháng đang chọn (YYYY-MM). */
  month?: string;
  /** Dòng hướng dẫn trong ô kéo thả. */
  hint: React.ReactNode;
  /** Đã ghi dữ liệu xong — nơi gọi tải lại dải lịch / danh sách file. */
  onApplied: (result: ImportResult) => void;
  /** Đang gửi lệnh áp dụng hay không — nơi gọi khoá ô chọn tháng trong lúc chờ. */
  onApplyingChange?: (applying: boolean) => void;
  /** Có thì ô chọn nhận nhiều file: chọn từ 2 file trở lên → giao cho nơi gọi (nhập hàng loạt). */
  onPickMany?: (files: File[]) => void;
}

/** Câu tóm tắt cho thông báo khi kết quả áp dụng về sau lúc người dùng đã rời khối nhập. */
const appliedSummary = (fileName: string, data: ImportResult) => {
  const period = data.periods[0];
  const when =
    data.kind === 'DAILY' && data.day
      ? `ngày ${formatDay(data.day)}`
      : period
      ? `giai đoạn ${formatPeriod(period.start, period.end)}`
      : '';
  const star = data.starEvaluation;
  const starText =
    data.kind !== 'DAILY'
      ? ''
      : star?.ok
      ? ` Đã chấm Sao: ${formatNumber(star.rewarded)} Creator, +${formatNumber(star.stars)} ⭐.`
      : ' Chưa chấm được Sao: vào Doanh thu → Data Kim cương → Chấm lại khoảng ngày.';
  return `File ${fileName}${when ? `, ${when}` : ''}.${starText} Xem chi tiết ở tab Lịch sử nhập.`;
};

/**
 * Luồng nhập một file "Dữ liệu nhà sáng tạo" của TikTok LIVE Backstage (đặc tả mục 9.2):
 * chọn file → tự xem trước (không ghi gì) → xem khối chặn / cảnh báo / bảng ghép UID →
 * áp dụng cùng file đó. Dùng chung cho tab Data ngày (DAILY) và Data tháng (MONTHLY).
 */
const ImportFlow = ({ kind, month, hint, onApplied, onApplyingChange, onPickMany }: Props) => {
  const { reportError } = useDataImport();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  // Tăng mỗi lần xem trước: bảng kết quả dựng lại (bộ lọc, trang) cho file mới.
  const [previewId, setPreviewId] = useState(0);
  const alive = useRef(true);
  // Chỉ nhận phản hồi của lần gửi mới nhất.
  const ticket = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const applying = busy === 'apply';
  useEffect(() => {
    if (!onApplyingChange) return undefined;
    onApplyingChange(applying);
    return () => onApplyingChange(false);
  }, [applying, onApplyingChange]);

  const send = async (step: 'preview' | 'apply', target: File, overrideReason?: string) => {
    const form = new FormData();
    form.append('file', target);
    form.append('kind', kind);
    if (kind === 'MONTHLY' && month) form.append('month', month);
    // Admin nhập file tháng khi tháng còn thiếu ngày (máy chủ tự kiểm quyền Admin).
    if (overrideReason) form.append('overrideReason', overrideReason);
    ticket.current += 1;
    const current = ticket.current;
    // Người dùng đã rời khối nhập (đổi tab, đổi tháng) hoặc đã gửi lần khác.
    const isStale = () => !alive.current || current !== ticket.current;
    setBusy(step);
    try {
      const res: any = await AxiosUtils.post(
        step === 'preview' ? API.dataImportPreview : API.dataImportApply,
        form,
        { 'Content-Type': 'multipart/form-data' }
      );
      const data = normalizeResult(res?.data);
      if (step === 'preview') {
        // Xem trước không ghi gì: kết quả về muộn thì bỏ.
        if (isStale()) return;
        setResult(data);
        setPreviewId((id) => id + 1);
        return;
      }
      // Áp dụng ĐÃ ghi dữ liệu: luôn báo kết quả, kể cả khi khối nhập không còn trên màn hình.
      if (isStale()) {
        notification.success({
          message: `Đã lưu ${formatNumber(
            data.summary.statsSaved ?? data.summary.rows
          )} dòng số liệu`,
          description: appliedSummary(target.name, data),
          duration: 8,
        });
      } else {
        setResult(data);
      }
      onApplied(data);
    } catch (err) {
      if (step === 'preview') {
        if (isStale()) return;
        setFile(null);
        reportError(err, 'Không đọc được file');
        return;
      }
      // Áp dụng lỗi: không được im lặng, nếu không người dùng tưởng đã nhập xong.
      reportError(err, isStale() ? `Nhập file ${target.name} thất bại` : 'Nhập dữ liệu thất bại');
    } finally {
      if (!isStale()) setBusy(null);
    }
  };

  /** antd gọi beforeUpload cho từng file, kèm cả danh sách: chỉ xử lý một lần ở file đầu. */
  const pickFiles = (picked: File, list: File[]) => {
    if (onPickMany && list.length > 1) {
      if (picked === list[0]) onPickMany(list);
      return false;
    }
    return pickFile(picked);
  };

  const pickFile = (picked: File) => {
    if (!/\.(xlsx|csv)$/i.test(picked.name)) {
      notification.error({
        message: 'File không đúng định dạng',
        description: 'Chỉ nhận file .xlsx hoặc .csv tải từ TikTok LIVE Backstage.',
      });
      return false;
    }
    if (picked.size > MAX_FILE_MB * 1024 * 1024) {
      notification.error({
        message: 'File quá lớn',
        description: `File tối đa ${MAX_FILE_MB} MB.`,
      });
      return false;
    }
    setFile(picked);
    setResult(null);
    send('preview', picked);
    return false; // không để antd tự upload
  };

  const reset = () => {
    ticket.current += 1;
    setFile(null);
    setResult(null);
    setBusy(null);
  };

  if (result) {
    return (
      <ImportResultPanel
        key={previewId}
        result={result}
        kind={kind}
        applying={applying}
        onApply={(overrideReason) => file && send('apply', file, overrideReason)}
        onReset={reset}
      />
    );
  }

  return (
    <Upload.Dragger
      accept=".xlsx,.csv"
      multiple={!!onPickMany}
      showUploadList={false}
      beforeUpload={pickFiles}
      disabled={!!busy}
    >
      <p className="ant-upload-drag-icon">{busy ? <LoadingOutlined /> : <InboxOutlined />}</p>
      <p className="ant-upload-text">
        {busy
          ? `Đang đọc file ${file?.name || ''}…`
          : onPickMany
          ? 'Kéo thả hoặc bấm để chọn một hay nhiều file .xlsx / .csv'
          : 'Kéo thả hoặc bấm để chọn file .xlsx / .csv'}
      </p>
      <p className="ant-upload-hint">{hint}</p>
    </Upload.Dragger>
  );
};

export default ImportFlow;
